// One slot of a caster, in Godsmode: a glass vessel with the icon of its
// spell. `vesselShapes.ts` has the outlines. A skill has its cost on a coin
// at the corner. The face is also a meter: it fills from the bottom as the energy of
// its side covers the cost, and it is solid when the side can pay. A skill
// that waits for half strength is faint, and so is an empty scroll slot.
// The caster select shows the same face with no energy and no click.

import {
  AURA_SPARK_ACROSS,
  AURA_SPARK_BIG_SHARE,
  AURA_SPARK_COUNT,
  AURA_SPARK_TIME_MS,
} from '../constants';
import type { Slot } from '../game/casters';
import { el } from '../lib/dom';
import { createRng, type Rng } from '../lib/rng';
import { spellById, spellClasses } from '../spells/library';
import type { SpellId } from '../spells/types';
import { createSpellIcon } from './SpellIcon';
import { vesselStyle } from './vesselShapes';

/** Plain words, not final copy. */
const EMPTY_SCROLL_TIP = 'An empty scroll slot.';
const LOCKED_TIP = 'It unlocks when the army is at half strength.';
const SCROLL_TIP = 'A scroll. It costs no energy and is used up.';

/** The `data-tip` fields of a slot. `Tooltip.ts` draws a tooltip with a title as the tooltip of a spell. */
const slotTip = (slot: Slot): Record<string, string> => {
  if (slot.spellId === null) return { tip: EMPTY_SCROLL_TIP };
  const spell = spellById(slot.spellId);
  // The cost of a skill is on the corner of its face, so the tooltip does not repeat it.
  const note = slot.kind === 'scroll' ? SCROLL_TIP : slot.locked ? LOCKED_TIP : '';
  return {
    tip: spell.blurb,
    tipTitle: spell.name,
    tipSpell: spell.id,
    ...(note ? { tipNote: note } : {}),
  };
};

/** The part of the cost the energy pays, from 0 to 1. A scroll is paid when the slot holds one. */
const paidShare = (slot: Slot, energy: number): number => {
  if (slot.kind === 'scroll') return slot.spellId === null ? 0 : 1;
  if (slot.locked) return 0;
  return slot.cost <= 0 ? 1 : Math.min(1, Math.max(0, energy) / slot.cost);
};

const between = (rng: Rng, [low, high]: readonly [number, number]): number =>
  low + rng.next() * (high - low);

/** One number from the letters of an id, as the seed of the pattern of that spell. */
const seedOf = (id: string): number =>
  Array.from(id).reduce(
    (seed, letter) => Math.imul(seed ^ letter.charCodeAt(0), 16777619),
    2166136261,
  );

/**
 * The sparks of pixels over a slot a click casts from. The pattern comes from
 * the id of the spell, so each spell has its own, and it is the same each
 * time the slot is drawn. `caster.css` has the look of a spark.
 */
const createAura = (spellId: SpellId): HTMLElement => {
  const rng = createRng(seedOf(spellId));
  const count = Math.round(between(rng, AURA_SPARK_COUNT));
  const bigShare = between(rng, AURA_SPARK_BIG_SHARE);
  const sparks = Array.from({ length: count }, () => {
    const time = Math.round(between(rng, AURA_SPARK_TIME_MS));
    const style = [
      `left: ${between(rng, AURA_SPARK_ACROSS).toFixed(1)}%`,
      `top: ${(rng.next() * 100).toFixed(1)}%`,
      `--spark-time: ${time}ms`,
      // A start in the past, so the sparks are out of step from the first frame.
      `--spark-delay: ${-Math.round(rng.next() * time)}ms`,
    ].join('; ');
    const className = rng.next() < bigShare ? 'slot__spark slot__spark--big' : 'slot__spark';
    return el('i', { className, style });
  });
  return el('span', { className: 'slot__aura', ariaHidden: 'true' }, sparks);
};

/**
 * The band of dither that crosses the face of a slot a click casts from, as
 * the glint crosses a royal piece. `caster.css` has the band. Each spell
 * starts at its own point of the cycle.
 */
const createGlint = (spellId: SpellId): HTMLElement =>
  el('span', {
    className: 'slot__glint',
    style: `--glint-shift: ${createRng(seedOf(spellId)).next().toFixed(2)}`,
  });

export type SlotFaceOptions = {
  slot: Slot;
  /** The energy of the side of the slot, as the view shows it. Null where no side has energy: the caster select. */
  energy: number | null;
  /** A click casts from the slot now. */
  open: boolean;
  /** Null for a face that is only read. Such a face is no button, so it can be inside one. */
  onCast: (() => void) | null;
};

/** The list item of one slot. The tooltip is on the item, because a button that is off gives no pointer events. */
export const createSlotFace = ({ slot, energy, open, onCast }: SlotFaceOptions): HTMLElement => {
  const spell = slot.spellId === null ? null : spellById(slot.spellId);
  const faint = slot.spellId === null || (slot.kind === 'skill' && slot.locked);
  const share = energy === null ? 0 : paidShare(slot, energy);
  const className = [
    'wheel__face',
    'slot',
    spell ? spellClasses('wheel__face', spell) : '',
    faint ? 'slot--faint' : '',
    share >= 1 ? 'slot--paid' : '',
    open ? 'slot--open' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const parts = [
    // With nothing paid there is no fill, so no edge of it shows at the foot of the face.
    share > 0 ? el('span', { className: 'slot__fill' }) : null,
    spell ? createSpellIcon(spell.id, 'wheel__glyph') : null,
    spell && open ? createGlint(spell.id) : null,
  ];
  const style = `--slot-share: ${share}`;
  const face =
    onCast === null
      ? el('span', { className: `${className} slot--still`, style }, parts)
      : el(
          'button',
          {
            className,
            type: 'button',
            disabled: !open,
            ariaLabel: spell ? spell.name : EMPTY_SCROLL_TIP,
            style,
            onclick: onCast,
          },
          parts,
        );
  // The slot has its sparks only while a click casts from it.
  const isPrimed = open && spell !== null;
  const slotClass = [
    'wheel__slot',
    isPrimed ? 'wheel__slot--open' : '',
    faint ? 'wheel__slot--faint' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return el(
    'li',
    {
      className: slotClass,
      style: vesselStyle(slot.kind, spell ? spell.family : null),
      dataset: slotTip(slot),
    },
    [
      face,
      // The cost is a digit on a coin. It is outside the face, because the outline of the vessel cuts the face.
      // The coin has the color of the side only when the side can pay.
      slot.kind === 'skill'
        ? el('span', {
            className: share >= 1 ? 'slot__cost slot__cost--paid' : 'slot__cost',
            textContent: String(slot.cost),
          })
        : null,
      isPrimed ? createAura(spell.id) : null,
    ],
  );
};
